import { Skeleton, Text, Title, Tooltip } from "@mantine/core";
import { Link } from "@tanstack/react-router";
import { Folder, Sparkles } from "lucide-react";
import type { ReactNode } from "react";

import { useListRecentFolders } from "../../api/generated/endpoints/assets/assets";
import { useGetMe } from "../../api/generated/endpoints/identity/identity";
import { useListBrands } from "../../api/generated/endpoints/teams/teams";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { teamRoute } from "../../platform/routes";
import editorImage from "../../ui/assets/home-editor.png";
import videoImage from "../../ui/assets/home-b-roll-image.png";
import imageImage from "../../ui/assets/home-simple-image.png";
import { GradientIcon } from "../../ui/GradientIcon";
import { PageColumns } from "../../ui/PageColumns";
import classes from "./HomeScreens.module.css";

/** Live's colors for the Sparkles beside the gradient "create" (its title, `home-title`). */
const SPARKLES = { from: "var(--mantine-color-indigo-6)", to: "var(--mantine-color-pink-5)" };

interface CardProps {
  title: string;
  description: string;
  image: string;
  /** The card's whole surface, as a link; without one the card is disabled. */
  link?: ((props: { className: string; children: ReactNode }) => ReactNode) | undefined;
  /** Why a card without a link does nothing. */
  note: string;
}

function HomeCard({ title, description, image, link, note }: CardProps) {
  const children = (
    <>
      <span className={classes.picture}>
        <img src={image} alt="" className={classes.image} />
      </span>
      <Text component="span" fw={700} size="md" className={classes.cardTitle}>
        {title}
      </Text>
      <Text component="span" size="sm" className={classes.cardText}>
        {description}
      </Text>
    </>
  );
  if (link !== undefined) return link({ className: classes.card ?? "", children });
  return (
    <Tooltip label={note}>
      <button type="button" className={classes.card} aria-disabled="true">
        {children}
      </button>
    </Tooltip>
  );
}

/** Live's five folder colours, one per place in the row (its `Folder1Icon` to `Folder5Icon`). */
const FOLDER_TONES = [
  "var(--mantine-color-indigo-5)",
  "var(--mantine-color-grape-5)",
  "var(--mantine-color-pink-4)",
  "var(--mantine-color-blue-5)",
  "var(--mantine-color-violet-3)",
];

/**
 * Live's "Recent Projects" (#247, captures gaps 09 to 11, A3c): the brand's folders this member
 * made, opened or saved into last, at most five, each with how many assets it holds; one opens
 * the library in it. The brand is the team's first, as the cards' links: none (`null`) for a
 * team without one, and not known yet (`undefined`) while the brands load, which shows the
 * skeletons rather than the empty words.
 */
function RecentProjects(props: { teamId: string; brandId: string | null | undefined }) {
  const { teamId, brandId } = props;
  const folders = useListRecentFolders(teamId, brandId ?? "", {
    query: { enabled: typeof brandId === "string" },
  });
  const items = brandId === null ? [] : folders.data;
  return (
    <section aria-labelledby="recent-projects" className={classes.recent}>
      <Title order={2} id="recent-projects" className={classes.recentTitle}>
        Recent Projects
      </Title>
      <div className={classes.recentRow}>
        {items === undefined && !folders.isError
          ? Array.from({ length: 5 }, (_, at) => (
              <Skeleton key={at} height={38} className={classes.recentItem} />
            ))
          : null}
        {folders.isError ? <ErrorAlert error={folders.error} /> : null}
        {items?.length === 0 ? (
          <Text className={classes.empty}>No recent projects yet</Text>
        ) : null}
        {typeof brandId !== "string"
          ? null
          : items?.map((folder, at) => (
              <Link
                key={folder.id}
                to="/teams/$teamId/brands/$brandId/library"
                params={{ teamId, brandId }}
                search={{ folder: folder.id }}
                className={classes.recentItem}
              >
                <span className={classes.recentIcon} style={{ color: FOLDER_TONES[at] }}>
                  <Folder
                    size={21}
                    strokeWidth={1.5}
                    fill="currentColor"
                    fillOpacity={0.3}
                    aria-hidden
                  />
                </span>
                <span className={classes.recentText}>
                  <span className={classes.recentName}>{folder.name}</span>
                  <span className={classes.recentMeta}>
                    {folder.asset_count} {folder.asset_count === 1 ? "asset" : "assets"}
                  </span>
                </span>
              </Link>
            ))}
      </div>
    </section>
  );
}

/**
 * The team's home (design-system.md 3.2): a greeting, the three ways to start, and the member's
 * Recent Projects. The links go to the team's first brand, as the sidebar's do.
 */
export function TeamHome() {
  const { teamId } = teamRoute.useParams();
  // Live greets by the full name (screenshot 01); an empty name says "there".
  const name = useGetMe({
    query: { select: (me) => me.name?.trim() || undefined },
  }).data;
  const brands = useListBrands(teamId).data;
  const brandId = brands?.[0]?.id;
  // The row's brand: not known while the brands load, none for a team without one.
  const recentBrand = brands === undefined ? undefined : (brandId ?? null);
  const studio = (medium: "video" | undefined) =>
    brandId === undefined
      ? undefined
      : ({ className, children }: { className: string; children: ReactNode }) => (
          <Link
            to="/teams/$teamId/brands/$brandId/generate"
            params={{ teamId, brandId }}
            search={medium === undefined ? {} : { medium }}
            className={className}
          >
            {children}
          </Link>
        );
  // The editor (E1) is built, so its card opens it, as live's does.
  const editor =
    brandId === undefined
      ? undefined
      : ({ className, children }: { className: string; children: ReactNode }) => (
          <Link
            to="/teams/$teamId/brands/$brandId/editor"
            params={{ teamId, brandId }}
            className={className}
          >
            {children}
          </Link>
        );
  const noBrand = "Add a brand first";
  return (
    <div className={classes.home}>
      <PageColumns bottom={<RecentProjects teamId={teamId} brandId={recentBrand} />}>
        <div className={classes.inner}>
          <Title order={1} className={classes.title}>
            Hey {name ?? "there"}! What would you like to{" "}
            <Text
              component="span"
              inherit
              variant="gradient"
              gradient={{ from: "indigo.5", to: "pink.5", deg: 70 }}
              className={classes.accent}
            >
              create
              <GradientIcon
                icon={Sparkles}
                size={25}
                colors={SPARKLES}
              />
            </Text>{" "}
            today?
          </Title>
          <div className={classes.cards}>
            <HomeCard
              title="Text to Image"
              description="Generate images with your choice of the latest AI models."
              image={imageImage}
              link={studio(undefined)}
              note={noBrand}
            />
            <HomeCard
              title="Turn into Video"
              description="Transform your prompt or image into a video."
              image={videoImage}
              link={studio("video")}
              note={noBrand}
            />
            <HomeCard
              title="AI Editor"
              description="Generate clips, edit footage, and assemble videos with full control."
              image={editorImage}
              link={editor}
              note={noBrand}
            />
          </div>
        </div>
      </PageColumns>
    </div>
  );
}
